import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 6px solid #000000;
  border-radius: 0;
  padding: 40px;
  width: 100%;
  max-width: 440px;
  box-shadow: 12px 12px 0px #000000;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
  
  &:hover {
    transform: rotate(0deg) scale(1.02);
    box-shadow: 16px 16px 0px #000000;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: #000000;
  text-align: center;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 4px 4px 0px #ff6b6b;
  font-family: 'Arial Black', Arial, sans-serif;
`;

const Subtitle = styled.p`
  color: #000000;
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.1rem;
  line-height: 1.5;
  font-weight: 700;
  background: #ffeb3b;
  padding: 8px 16px;
  border: 3px solid #000000;
  transform: rotate(1deg);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 15px;
  border: 4px solid #000000;
  border-radius: 0;
  background: #ffffff;
  color: #000000;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 4px 4px 0px #4ecdc4;
  
  &::placeholder {
    color: #666666;
    font-weight: 600;
  }
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    background: #f8f8f8;
    box-shadow: 6px 6px 0px #ff6b6b;
    transform: translate(-2px, -2px);
  }
`;

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

const Button = styled.button`
  padding: 18px;
  background: #45b7d1;
  border: 4px solid #000000;
  border-radius: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 6px 6px 0px #000000;
  font-family: 'Arial Black', Arial, sans-serif;
  
  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: 8px 8px 0px #000000;
    background: #96ceb4;
  }
  
  &:active {
    transform: translate(2px, 2px);
    box-shadow: 2px 2px 0px #000000;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    background: #cccccc;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 20px;
  color: #000000;
  font-weight: 700;
  
  a {
    color: #ff6b6b;
    text-decoration: none;
    font-weight: 900;
    background: #ffeb3b;
    padding: 2px 6px;
    border: 2px solid #000000;
    
    &:hover {
      background: #4ecdc4;
      color: #000000;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #ff6b6b;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 15px;
  color: #000000;
  text-align: center;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  transform: rotate(-1deg);
`;

const SuccessMessage = styled.div`
  background: #96ceb4;
  border: 4px solid #000000;
  border-radius: 0;
  padding: 15px;
  color: #000000;
  text-align: center;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 4px 4px 0px #000000;
  transform: rotate(1deg);
`;

const HelpText = styled.div`
  background: #ffeaa7;
  border: 2px solid #000000;
  border-radius: 0;
  padding: 12px;
  color: #000000;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 20px;
  font-weight: 700;
`;

function Register() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    planetCode: '',
    planetNickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/register', formData);
      
      if (response.data.success) {
        setSuccess('恭喜您成为尊贵会员！正在为您开启专属服务...');
        
        // 自动登录用户
        login(response.data.user, response.data.token);
        
        // 1秒后跳转到主页面
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('注册失败:', error);
      setError(error.response?.data?.message || '注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>👑 尊贵会员注册</Title>
        <Subtitle>成为梭哈AI知识星球的尊贵会员，享受专属特权</Subtitle>
        
        <HelpText>
          💡 注册须知：<br/>
          • 需要提供你在我的知识星球（梭哈 AI）编号和昵称进行验证<br/>
          • 如需加入星球或咨询，请添加微信：zlbigger
        </HelpText>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            name="username"
            placeholder="用户名"
            value={formData.username}
            onChange={handleChange}
            required
          />
          
          <Input
            type="password"
            name="password"
            placeholder="密码"
            value={formData.password}
            onChange={handleChange}
            required
          />
          
          <InputGroup>
            <Input
              type="text"
              name="planetCode"
              placeholder="星球编号（如：1984）"
              value={formData.planetCode}
              onChange={handleChange}
              required
            />
            
            <Input
              type="text"
              name="planetNickname"
              placeholder="星球昵称"
              value={formData.planetNickname}
              onChange={handleChange}
              required
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading}>
            {loading ? '正在开通会员身份...' : '成为尊贵会员'}
          </Button>
        </Form>
        
        <LinkText>
          已有账户？ <Link to="/login">立即登录</Link>
        </LinkText>
      </Card>
    </Container>
  );
}

export default Register;